import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import ImageModal from 'components/organisms/ImageModal/ImageModal';
import { memo, useCallback, useState } from 'react';
import {
  ImageContainer,
  ImageCounter,
  ImageLabel,
  ImageNavigationContainer,
  NavigationButton,
  ProductCardImageContainer,
} from './ProductCardImageSimple.styled';

type ProductCardImageProps = {
  images: string[];
  isLkpOffer: boolean;
  alt?: string;
  productName?: string;
  showNavigation?: boolean;
  showLabel?: boolean;
  enableModal?: boolean;
  onClick?: () => void;
};

const generateImageLabel = (hasImages: boolean, isLkpOffer: boolean): string =>
  hasImages && !isLkpOffer ? 'Imagem do produtor' : 'Imagem ilustrativa';

const calculateNavigationState = (
  currentIndex: number,
  totalImages: number,
) => ({
  canGoBack: currentIndex > 0,
  canGoForward: currentIndex < totalImages - 1,
  hasMultiple: totalImages > 1,
});

function ProductCardImage({
  images,
  isLkpOffer,
  alt = 'imagem do produto',
  productName,
  showNavigation = true,
  showLabel = true,
  enableModal = true,
  onClick,
}: ProductCardImageProps) {
  const [imageDisplay, setImageDisplay] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const handleImageDisplay = useCallback(
    (direction: 'prev' | 'next') => {
      const navigationMap = {
        prev: (current: number) =>
          current > 0 ? current - 1 : images.length - 1,
        next: (current: number) =>
          current < images.length - 1 ? current + 1 : 0,
      } as const;

      setImageDisplay(navigationMap[direction]);
    },
    [images.length],
  );

  const openModal = useCallback(() => setIsModalOpen(true), []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);

  const handleImageClick = useCallback(() => {
    if (onClick) {
      onClick();
    } else if (enableModal && images.length > 0) {
      openModal();
    }
  }, [enableModal, images.length, openModal, onClick]);

  const currentImage = images[imageDisplay] || '';
  const navigationState = calculateNavigationState(imageDisplay, images.length);
  const imageLabel = generateImageLabel(images.length > 0, isLkpOffer);

  return (
    <>
      <ProductCardImageContainer className="product-card-image">
        <ImageNavigationContainer>
          {navigationState.hasMultiple && showNavigation && (
            <NavigationButton
              type="text"
              size="small"
              icon={<LeftOutlined />}
              onClick={() => handleImageDisplay('prev')}
              aria-label="Imagem anterior"
            />
          )}

          <ImageContainer
            onClick={handleImageClick}
            style={{ cursor: enableModal || onClick ? 'pointer' : 'default' }}
            role={enableModal || onClick ? 'button' : undefined}
            tabIndex={enableModal || onClick ? 0 : undefined}
            aria-label={enableModal ? 'Abrir galeria de imagens' : alt}
          >
            <img
              src={currentImage}
              alt={alt}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
              loading="lazy"
            />

            {navigationState.hasMultiple && (
              <ImageCounter>
                {imageDisplay + 1}/{images.length}
              </ImageCounter>
            )}
          </ImageContainer>

          {navigationState.hasMultiple && showNavigation && (
            <NavigationButton
              type="text"
              size="small"
              icon={<RightOutlined />}
              onClick={() => handleImageDisplay('next')}
              aria-label="Próxima imagem"
            />
          )}
        </ImageNavigationContainer>

        {showLabel && <ImageLabel>{imageLabel}</ImageLabel>}
      </ProductCardImageContainer>

      {enableModal && (
        <ImageModal
          images={images}
          isOpen={isModalOpen}
          title={productName || 'Imagens do produto'}
          onClose={closeModal}
        />
      )}
    </>
  );
}

export default memo(ProductCardImage);
